import { useLocation } from 'wouter';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import {
  Minus,
  Plus,
  Calendar,
  MapPin,
  Users,
  CreditCard,
  Check,
  X,
  Coffee,
  Lock,
} from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import type {
  CheckoutAttendeeDraft,
  CheckoutSessionSummaryDto,
  CheckoutPromoSummaryDto,
} from '@shared/api-contracts';
import {
  type CancellationPolicy,
  getCancellationPolicyDescription,
  getCancellationPolicyLabel,
} from '@shared/cancellation-policy';

import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { PublicShell } from '@/components/public-shell';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { authedJson } from '@/lib/http';
import { redirectToLogin } from '@/lib/authUtils';
import { formatDateWithTimezone } from '@/lib/dateUtils';
import { normalizeImageUrl, imageErrorFallback, PLACEHOLDER } from '@/lib/image';
import { trackPublicFunnelEvent } from '@/lib/public-analytics';

const stripePromise = import.meta.env.VITE_STRIPE_PUBLIC_KEY
  ? loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY)
  : null;

type ClassItemInfo = {
  id: string;
  title: string;
  price: string | number;
  pricingMode?: string | null;
  suggestedPrice?: string | number | null;
  type?: 'class';
  thumbnailUrl?: string;
  creator?: { displayName?: string };
  category?: string;
  duration?: number;
  location?: string;
  cancellationPolicy?: string;
};

type TipItemInfo = {
  id: string;
  title: string;
  price: string | number;
  type?: 'tip';
  displayName?: string;
  creatorHeadline?: string;
};

type CheckoutSessionInfo = {
  id: string;
  availableSpots?: number | null;
  startDateTime: string;
  endDateTime: string;
  notes?: string;
  maxSelectableQuantity?: number;
};

type PaymentIntentResponse = {
  paymentIntentId?: string | null;
  clientSecret: string | null;
  amount?: number;
  classTitle?: string;
  sessionDateTime?: string;
};

type HydratedCheckoutSession = {
  summary: CheckoutSessionSummaryDto;
  paymentIntent: PaymentIntentResponse | null;
};

function extractPaymentIntentIdFromClientSecret(
  clientSecret: string | null | undefined
): string | null {
  if (!clientSecret) {
    return null;
  }

  const [paymentIntentId] = clientSecret.split('_secret_');
  return paymentIntentId || null;
}

function replaceCheckoutUrlState(checkoutSessionId: string, paymentIntentId?: string | null) {
  const url = new window.URL(window.location.href);

  url.searchParams.set('checkout_session_id', checkoutSessionId);
  url.searchParams.set('type', 'class');

  if (paymentIntentId) {
    url.searchParams.set('payment_intent', paymentIntentId);
  } else {
    url.searchParams.delete('payment_intent');
  }

  url.searchParams.delete('redirect_status');
  url.searchParams.delete('payment_intent_client_secret');
  url.searchParams.delete('client_secret');
  url.searchParams.delete('creator_id');

  const nextSearch = url.searchParams.toString();
  const nextUrl = `${url.pathname}${nextSearch ? `?${nextSearch}` : ''}${url.hash}`;

  window.history.replaceState(window.history.state, '', nextUrl);
}

function CheckoutForm({
  checkoutSessionId,
  isProcessing,
  setIsProcessing,
  isBlocked = false,
  onBeforeSubmit,
  returnUrl,
}: {
  checkoutSessionId?: string;
  isProcessing: boolean;
  setIsProcessing: (processing: boolean) => void;
  isBlocked?: boolean;
  onBeforeSubmit?: () => Promise<boolean>;
  returnUrl?: string;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);

    if (!stripe || !elements) {
      setIsProcessing(false);
      return;
    }

    if (onBeforeSubmit) {
      const canContinue = await onBeforeSubmit();
      if (!canContinue) {
        setIsProcessing(false);
        return;
      }
    }

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        // Return to checkout so we can run the local confirmation fallback if webhooks are unavailable
        return_url:
          returnUrl ??
          (checkoutSessionId
            ? `${window.location.origin}/checkout?checkout_session_id=${checkoutSessionId}`
            : `${window.location.origin}/checkout`),
      },
    });

    if (error) {
      toast({
        title: 'Payment Failed',
        description: error.message || 'There was an issue processing your payment.',
        variant: 'destructive',
      });
    } else {
      toast({
        title: 'Payment Successful',
        description: 'Thank you for your purchase!',
      });
    }

    setIsProcessing(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <PaymentElement />
      {isBlocked && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          Complete the guest name fields before submitting payment.
        </div>
      )}
      <Button
        type="submit"
        disabled={!stripe || isProcessing || isBlocked}
        className="w-full rounded-full py-6 text-base font-semibold"
        data-testid="button-complete-payment"
      >
        <CreditCard className="mr-2 h-5 w-5" />
        {isProcessing ? 'Processing...' : 'Complete payment'}
      </Button>
    </form>
  );
}

export default function Checkout() {
  const [, navigate] = useLocation();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { toast } = useToast();
  const handledRedirectRef = useRef<string | null>(null);
  const completedCheckoutRef = useRef(false);
  const checkoutDropoffTrackedRef = useRef(false);
  const checkoutAnalyticsRef = useRef<{
    checkoutSessionId: string;
    paymentType: string;
    classId?: string;
    sessionId?: string;
    quantity?: number;
  }>({
    checkoutSessionId: '',
    paymentType: '',
  });
  const [checkoutSessionId, setCheckoutSessionId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [paymentType, setPaymentType] = useState('');
  const [itemInfo, setItemInfo] = useState<ClassItemInfo | TipItemInfo | null>(null);
  const [sessionInfo, setSessionInfo] = useState<CheckoutSessionInfo | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [unitPrice, setUnitPrice] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [maxQuantity, setMaxQuantity] = useState(10);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(true);
  const [isUpdatingQuantity, setIsUpdatingQuantity] = useState(false);
  const [attendeeDrafts, setAttendeeDrafts] = useState<CheckoutAttendeeDraft[]>([]);
  const [promoCode, setPromoCode] = useState('');
  const [appliedPromoCode, setAppliedPromoCode] = useState<CheckoutPromoSummaryDto | null>(null);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [isValidatingPromo, setIsValidatingPromo] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [customerAmount, setCustomerAmount] = useState('');
  const [isUpdatingCustomerAmount, setIsUpdatingCustomerAmount] = useState(false);
  // Set when Stripe redirects back after a successful tip payment.
  const [tipCompleted, setTipCompleted] = useState<{
    creatorId: string | null;
    amountCents: number | null;
  } | null>(null);
  // Set when Stripe redirects back after a successful pass purchase.
  const [passCompleted, setPassCompleted] = useState<{
    classId: string | null;
    amountCents: number | null;
  } | null>(null);
  // Pass cart state (promo is entered here, like normal checkout).
  const [passClassId, setPassClassId] = useState<string | null>(null);
  // Value is write-only (kept for a future "original price" display); only the setter is used.
  const [, setPassOriginalCents] = useState(0);
  const [passPromoApplied, setPassPromoApplied] = useState<string | null>(null);
  const [passPaymentIntentId, setPassPaymentIntentId] = useState<string | null>(null);
  // True when a promo discounts the pass to free — show a "Get free pass" button
  // instead of the card form (no charge needed).
  const [passIsFree, setPassIsFree] = useState(false);
  const [isCompletingFreePass, setIsCompletingFreePass] = useState(false);

  // Calculate if the order is free. Tips are never "free" — they always carry
  // a payment intent, even though there's no class pricing snapshot behind them.
  const finalTotal = Math.max(totalAmount - discountAmount, 0);
  const isFreeOrder = paymentType !== 'tip' && paymentType !== 'pass' && finalTotal <= 0;
  const requiredGuestCount = Math.max(quantity - 1, 0);
  const attendeeDraftsComplete =
    requiredGuestCount === 0 ||
    (attendeeDrafts.length >= requiredGuestCount &&
      attendeeDrafts.slice(0, requiredGuestCount).every(draft => draft.name.trim().length > 0));
  const needsGuestNames = paymentType === 'class' && requiredGuestCount > 0;

  useEffect(() => {
    checkoutAnalyticsRef.current = {
      checkoutSessionId,
      paymentType,
      classId: paymentType === 'class' ? itemInfo?.id : undefined,
      sessionId: paymentType === 'class' ? sessionInfo?.id : undefined,
      quantity,
    };
  }, [checkoutSessionId, itemInfo?.id, paymentType, quantity, sessionInfo?.id]);

  const emitCheckoutDropoff = () => {
    if (checkoutDropoffTrackedRef.current || completedCheckoutRef.current) {
      return;
    }

    const current = checkoutAnalyticsRef.current;
    if (current.paymentType !== 'class' || !current.checkoutSessionId) {
      return;
    }

    checkoutDropoffTrackedRef.current = true;
    void trackPublicFunnelEvent({
      eventName: 'checkout_dropoff',
      source: 'checkout_page',
      destinationType: 'exit',
      checkoutSessionId: current.checkoutSessionId,
      classId: current.classId,
      sessionId: current.sessionId,
      quantity: current.quantity,
      keepalive: true,
    });
  };

  useEffect(() => {
    const handlePageHide = () => {
      emitCheckoutDropoff();
    };

    window.addEventListener('pagehide', handlePageHide);
    return () => {
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, []);

  useEffect(() => {
    return () => {
      emitCheckoutDropoff();
    };
  }, []);

  const applyHydratedCheckoutSession = (
    { summary, paymentIntent }: HydratedCheckoutSession,
    options?: { preserveClientSecret?: boolean }
  ) => {
    const preservedPaymentIntentId = options?.preserveClientSecret
      ? new window.URLSearchParams(window.location.search).get('payment_intent') ||
        extractPaymentIntentIdFromClientSecret(clientSecret)
      : null;
    const resolvedPaymentIntentId =
      paymentIntent?.paymentIntentId ??
      extractPaymentIntentIdFromClientSecret(paymentIntent?.clientSecret) ??
      preservedPaymentIntentId;

    setCheckoutSessionId(summary.id);
    setPaymentType('class');
    setItemInfo({
      id: summary.class.id,
      title: summary.class.title,
      price: summary.class.price,
      pricingMode: summary.class.pricingMode ?? 'standard',
      suggestedPrice: summary.class.suggestedPrice ?? null,
      thumbnailUrl: summary.class.thumbnailUrl ?? undefined,
      category: summary.class.category ?? undefined,
      duration: summary.class.duration ?? undefined,
      location: summary.class.location ?? undefined,
      cancellationPolicy: summary.class.cancellationPolicy,
      creator: summary.class.creator
        ? { displayName: summary.class.creator.displayName }
        : undefined,
    });
    setSessionInfo({
      id: summary.session.id,
      startDateTime: summary.session.startDateTime,
      endDateTime: summary.session.endDateTime,
      notes: summary.session.notes ?? undefined,
      availableSpots: summary.session.remainingSpots,
      maxSelectableQuantity: summary.session.maxSelectableQuantity,
    });
    setQuantity(summary.quantity);
    setUnitPrice(summary.pricingSnapshot.unitPrice);
    setTotalAmount(summary.pricingSnapshot.subtotal);
    setDiscountAmount(summary.pricingSnapshot.discountAmount);
    setAppliedPromoCode(summary.appliedPromoCode);
    setAttendeeDrafts(summary.attendeeDrafts ?? []);
    setMaxQuantity(Math.max(summary.session.maxSelectableQuantity, 1));
    replaceCheckoutUrlState(summary.id, resolvedPaymentIntentId);
    if (paymentIntent?.clientSecret || !options?.preserveClientSecret) {
      setClientSecret(paymentIntent?.clientSecret ?? '');
    }
  };

  const hydrateCheckoutSession = async (id: string): Promise<HydratedCheckoutSession> => {
    const currentPaymentIntentId =
      new window.URLSearchParams(window.location.search).get('payment_intent') ||
      extractPaymentIntentIdFromClientSecret(clientSecret);
    const summary = await authedJson<CheckoutSessionSummaryDto>(`/api/checkout/sessions/${id}`);

    if (summary.pricingSnapshot.finalAmount <= 0 && !currentPaymentIntentId) {
      return {
        summary,
        paymentIntent: null,
      };
    }

    const paymentIntent = await authedJson<PaymentIntentResponse>(
      `/api/checkout/sessions/${id}/payment-intent`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          paymentIntentId: currentPaymentIntentId,
        }),
      }
    );

    return {
      summary,
      paymentIntent,
    };
  };

  const updateClassCheckoutSession = async (
    id: string,
    updates: {
      quantity?: number;
      promoCode?: string | null;
      attendeeDrafts?: CheckoutAttendeeDraft[];
      customerAmount?: number;
    }
  ): Promise<HydratedCheckoutSession> => {
    const currentPaymentIntentId =
      new window.URLSearchParams(window.location.search).get('payment_intent') ||
      extractPaymentIntentIdFromClientSecret(clientSecret);
    const summary = await authedJson<CheckoutSessionSummaryDto>(`/api/checkout/sessions/${id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updates),
    });

    if (summary.pricingSnapshot.finalAmount <= 0 && !currentPaymentIntentId) {
      return {
        summary,
        paymentIntent: null,
      };
    }

    const paymentIntent = await authedJson<PaymentIntentResponse>(
      `/api/checkout/sessions/${id}/payment-intent`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          paymentIntentId: currentPaymentIntentId,
        }),
      }
    );

    return {
      summary,
      paymentIntent,
    };
  };

  const saveAttendeeDrafts = async (): Promise<boolean> => {
    if (paymentType !== 'class' || !checkoutSessionId) {
      return true;
    }

    if (!attendeeDraftsComplete) {
      toast({
        title: 'Guest Names Required',
        description: 'Please add a guest name for each additional seat before continuing.',
        variant: 'destructive',
      });
      return false;
    }

    if (requiredGuestCount === 0) {
      return true;
    }

    const summary = await authedJson<CheckoutSessionSummaryDto>(
      `/api/checkout/sessions/${checkoutSessionId}`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          attendeeDrafts: attendeeDrafts.slice(0, requiredGuestCount).map(draft => ({
            name: draft.name.trim(),
            isGuest: true as const,
          })),
        }),
      }
    );

    applyHydratedCheckoutSession(
      {
        summary,
        paymentIntent: null,
      },
      { preserveClientSecret: true }
    );

    return true;
  };

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      toast({
        title: 'Unauthorized',
        description: 'Please log in to complete your purchase.',
        variant: 'destructive',
      });
      redirectToLogin(500);
      return;
    }
  }, [isAuthenticated, authLoading, toast]);

  useEffect(() => {
    let isActive = true;

    const initializeCheckout = async () => {
      setIsCheckoutLoading(true);

      const urlParams = new window.URLSearchParams(window.location.search);
      const secret = urlParams.get('client_secret');
      const paymentIntent = urlParams.get('payment_intent');
      const type = urlParams.get('type');
      const creatorId = urlParams.get('creator_id');
      const sessionId = urlParams.get('checkout_session_id');

      if (paymentIntent && !sessionId) {
        return;
      }

      try {
        if (sessionId) {
          const hydratedCheckout = await hydrateCheckoutSession(sessionId);
          if (!isActive) {
            return;
          }

          applyHydratedCheckoutSession(hydratedCheckout);
          setMaxQuantity(Math.max(hydratedCheckout.summary.session.maxSelectableQuantity, 1));
          return;
        }

        if (type === 'tip' && creatorId && secret) {
          const data = await authedJson<TipItemInfo>(`/api/creators/${creatorId}`);
          if (!isActive) {
            return;
          }

          setPaymentType('tip');
          setClientSecret(secret);
          setItemInfo(data);
          const tipAmountCents = parseInt(urlParams.get('amount') ?? '', 10);
          if (Number.isFinite(tipAmountCents) && tipAmountCents > 0) {
            setTotalAmount(tipAmountCents / 100);
            setUnitPrice(tipAmountCents / 100);
          }
          return;
        }

        if (type === 'pass' && urlParams.get('class_id')) {
          const clsId = urlParams.get('class_id')!;
          const data = await authedJson<ClassItemInfo>(`/api/classes/${clsId}`);
          if (!isActive) return;
          // Create the pass PaymentIntent here (no promo yet) so the promo can be
          // entered in the cart, consistent with normal checkout.
          const purchaseRes = await apiRequest('POST', `/api/classes/${clsId}/passes/purchase`, {});
          const pj = await purchaseRes.json();
          if (!isActive) return;
          if (pj?.type === 'pass' && pj.clientSecret) {
            setPaymentType('pass');
            setPassClassId(clsId);
            setClientSecret(pj.clientSecret);
            setPassPaymentIntentId(pj.paymentIntentId ?? null);
            setItemInfo(data);
            const original = Number(pj.originalAmount ?? pj.amount) || 0;
            setPassOriginalCents(original);
            setTotalAmount((pj.amount ?? original) / 100);
            setUnitPrice((pj.amount ?? original) / 100);
          } else if (pj?.pass) {
            // Free pass (price 0) — straight to confirmation.
            setPassCompleted({ classId: clsId, amountCents: 0 });
            setIsCheckoutLoading(false);
          } else {
            toast({
              title: 'Could not start pass checkout',
              description: pj?.error || 'Please try again.',
              variant: 'destructive',
            });
            navigate(`/classes/${clsId}`);
          }
          return;
        }

        toast({
          title: 'Invalid Checkout',
          description: 'Missing payment information. Please try again.',
          variant: 'destructive',
        });
        navigate('/');
      } catch (error) {
        if (!isActive) {
          return;
        }

        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        const isExpiredCheckout =
          errorMessage.includes('SESSION_EXPIRED') ||
          errorMessage.includes('expired') ||
          errorMessage.startsWith('409:');

        toast({
          title: isExpiredCheckout ? 'Checkout Expired' : 'Checkout Unavailable',
          description: isExpiredCheckout
            ? 'Your checkout session expired. Please start again.'
            : 'Failed to load your checkout. Please try again.',
          variant: 'destructive',
        });
        navigate('/classes');
      } finally {
        if (isActive) {
          setIsCheckoutLoading(false);
        }
      }
    };

    void initializeCheckout();

    return () => {
      isActive = false;
    };
  }, [navigate, toast]);

  // Detect when Stripe redirects back after successful payment
  useEffect(() => {
    const urlParams = new window.URLSearchParams(window.location.search);
    const paymentIntentParam = urlParams.get('payment_intent');
    const redirectStatus = urlParams.get('redirect_status');
    const sessionId = urlParams.get('checkout_session_id');

    if (!paymentIntentParam || redirectStatus !== 'succeeded') {
      return;
    }

    if (!sessionId) {
      // Tip payments return here (no checkout session). The webhook finalizes
      // the tip server-side; show the confirmation instead of bouncing home.
      if (urlParams.get('type') === 'tip') {
        if (handledRedirectRef.current === paymentIntentParam) {
          return;
        }
        handledRedirectRef.current = paymentIntentParam;
        const amountCents = parseInt(urlParams.get('amount') ?? '', 10);
        setTipCompleted({
          creatorId: urlParams.get('creator_id'),
          amountCents: Number.isFinite(amountCents) && amountCents > 0 ? amountCents : null,
        });
        setIsCheckoutLoading(false);
        toast({
          title: 'Tip sent!',
          description: 'Thank you for supporting this creator.',
          duration: 5000,
        });
        return;
      }
      // Pass purchases return here (no checkout session). The webhook finalizes
      // the pass server-side; show the confirmation.
      if (urlParams.get('type') === 'pass') {
        if (handledRedirectRef.current === paymentIntentParam) {
          return;
        }
        handledRedirectRef.current = paymentIntentParam;
        const amountCents = parseInt(urlParams.get('amount') ?? '', 10);
        setPassCompleted({
          classId: urlParams.get('class_id'),
          amountCents: Number.isFinite(amountCents) && amountCents > 0 ? amountCents : null,
        });
        setIsCheckoutLoading(false);
        toast({
          title: 'Pass purchased!',
          description: 'Your punch pass is ready to use.',
          duration: 5000,
        });
        return;
      }
      navigate(`/?payment_intent=${paymentIntentParam}&redirect_status=${redirectStatus}`);
      return;
    }

    if (handledRedirectRef.current === paymentIntentParam) {
      return;
    }

    handledRedirectRef.current = paymentIntentParam;
    setIsProcessing(true);

    apiRequest('POST', `/api/checkout/sessions/${sessionId}/confirm`, {
      paymentIntentId: paymentIntentParam,
    })
      .then(response => response.json())
      .then(async () => {
        await Promise.all([
          queryClient.refetchQueries({ queryKey: ['/api/classes'] }),
          queryClient.refetchQueries({ queryKey: ['/api/classes/featured'] }),
        ]);

        queryClient.invalidateQueries({
          predicate: query => {
            const queryKeyHead = query.queryKey[0]?.toString() ?? '';
            return (
              queryKeyHead.startsWith('/api/enrollments') ||
              (queryKeyHead.includes('/api/classes/') && queryKeyHead.includes('/sessions'))
            );
          },
        });

        toast({
          title: 'Purchase Successful!',
          description: 'Thank you for your purchase. You have been successfully enrolled.',
          duration: 5000,
        });

        completedCheckoutRef.current = true;
        navigate(`/checkout/confirmation?checkout_session_id=${sessionId}`);
      })
      .catch(error => {
        toast({
          title: 'Enrollment Processing',
          description:
            error instanceof Error
              ? error.message
              : 'Your payment was successful. Your enrollment will be processed shortly.',
          duration: 5000,
        });
        completedCheckoutRef.current = true;
        navigate(`/checkout/confirmation?checkout_session_id=${sessionId}`);
      })
      .finally(() => {
        setIsProcessing(false);
      });
  }, [navigate, toast]);

  // Mutation for updating quantity and payment intent
  const updateQuantityMutation = useMutation<HydratedCheckoutSession, Error, number>({
    mutationFn: async (newQuantity: number) => {
      if (paymentType !== 'class' || !checkoutSessionId) {
        throw new Error('Checkout session not loaded');
      }

      return updateClassCheckoutSession(checkoutSessionId, {
        quantity: newQuantity,
      });
    },
    onSuccess: hydratedCheckout => {
      applyHydratedCheckoutSession(hydratedCheckout);
      setPromoError('');
      toast({
        title: 'Cart Updated',
        description: 'Your order has been updated successfully.',
      });
    },
    onError: error => {
      toast({
        title: 'Update Failed',
        description: error.message || 'Failed to update your order. Please try again.',
        variant: 'destructive',
      });
    },
    onSettled: () => {
      setIsUpdatingQuantity(false);
    },
  });

  const validatePromoCodeMutation = useMutation<HydratedCheckoutSession, Error, string>({
    mutationFn: async (code: string) => {
      if (paymentType !== 'class' || !checkoutSessionId) {
        throw new Error('Checkout session not loaded');
      }

      return updateClassCheckoutSession(checkoutSessionId, {
        promoCode: code,
      });
    },
    onSuccess: hydratedCheckout => {
      applyHydratedCheckoutSession(hydratedCheckout);
      setPromoError('');
      setPromoCode('');
      toast({
        title: 'Promo Code Applied',
        description: `You saved $${hydratedCheckout.summary.pricingSnapshot.discountAmount.toFixed(2)}!`,
      });
    },
    onError: error => {
      setPromoError(error.message.replace(/^\d+:\s*/, '') || 'Failed to validate promo code');
      setAppliedPromoCode(null);
      setDiscountAmount(0);
    },
    onSettled: () => {
      setIsValidatingPromo(false);
    },
  });

  const freeEnrollmentMutation = useMutation<unknown, Error>({
    mutationFn: async () => {
      if (paymentType !== 'class' || !checkoutSessionId) {
        throw new Error('Checkout session not loaded');
      }

      const response = await apiRequest(
        'POST',
        `/api/checkout/sessions/${checkoutSessionId}/complete-free`
      );
      return response.json() as Promise<unknown>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: query => (query.queryKey[0]?.toString() ?? '').startsWith('/api/enrollments'),
      });
      toast({
        title: 'Enrollment Complete',
        description: 'You have been successfully enrolled in this free class!',
      });
      completedCheckoutRef.current = true;
      setTimeout(() => {
        navigate(`/checkout/confirmation?checkout_session_id=${checkoutSessionId}`);
      }, 1500);
    },
    onError: (error: Error) => {
      toast({
        title: 'Enrollment Failed',
        description: `Failed to complete your free enrollment: ${error.message || 'Unknown error'}`,
        variant: 'destructive',
      });
    },
    onSettled: () => {
      setIsProcessing(false);
    },
  });

  const handleFreeEnrollment = async () => {
    const canContinue = await saveAttendeeDrafts();
    if (!canContinue) {
      return;
    }

    setIsProcessing(true);
    freeEnrollmentMutation.mutate();
  };

  const handleAttendeeDraftChange = (index: number, name: string) => {
    setAttendeeDrafts(current => {
      const nextDrafts = Array.from(
        { length: Math.max(requiredGuestCount, index + 1) },
        (_, draftIndex) => current[draftIndex] ?? { name: '', isGuest: true as const }
      );

      nextDrafts[index] = {
        name,
        isGuest: true,
      };

      return nextDrafts;
    });
  };

  const handleApplyPromoCode = () => {
    if (!promoCode.trim()) return;
    setIsValidatingPromo(true);
    setPromoError('');
    validatePromoCodeMutation.mutate(promoCode.trim().toUpperCase());
  };

  // Pass promo: re-price the pending checkout WITHOUT finalizing. A partial
  // discount updates the PaymentIntent amount; a 100%-off code flips the cart to
  // a free state with a "Get free pass" button (the user still confirms).
  const handleApplyPassPromo = async () => {
    if (!passClassId || !promoCode.trim()) return;
    setIsValidatingPromo(true);
    setPromoError('');
    try {
      const res = await apiRequest('POST', `/api/classes/${passClassId}/passes/reprice`, {
        paymentIntentId: passPaymentIntentId,
        promoCode: promoCode.trim().toUpperCase(),
      });
      const pj = await res.json();
      const code = pj.promoCode ?? promoCode.trim().toUpperCase();
      setPassPromoApplied(code);
      setDiscountAmount((pj.discount ?? 0) / 100);
      setPassIsFree(!!pj.free);
    } catch (err) {
      setPromoError(err instanceof Error ? err.message : 'Invalid promo code');
    } finally {
      setIsValidatingPromo(false);
    }
  };

  const handleRemovePassPromo = async () => {
    if (!passClassId) return;
    setPromoError('');
    setPromoCode('');
    setIsValidatingPromo(true);
    try {
      await apiRequest('POST', `/api/classes/${passClassId}/passes/reprice`, {
        paymentIntentId: passPaymentIntentId,
        promoCode: '',
      });
      setDiscountAmount(0);
      setPassPromoApplied(null);
      setPassIsFree(false);
    } catch {
      // leave current state
    } finally {
      setIsValidatingPromo(false);
    }
  };

  // Complete a free (100%-off) pass — the only point a free pass is created.
  const handleCompleteFreePass = async () => {
    if (!passClassId) return;
    setIsCompletingFreePass(true);
    try {
      const res = await apiRequest('POST', `/api/classes/${passClassId}/passes/purchase`, {
        promoCode: passPromoApplied ?? undefined,
      });
      const pj = await res.json();
      if (pj?.pass) {
        setPassCompleted({ classId: passClassId, amountCents: 0 });
      } else {
        toast({
          title: 'Could not complete',
          description: pj?.error || 'Please try again.',
          variant: 'destructive',
        });
      }
    } catch (err) {
      toast({
        title: 'Could not complete',
        description: err instanceof Error ? err.message : 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsCompletingFreePass(false);
    }
  };

  const handleRemovePromoCode = () => {
    setPromoError('');
    setPromoCode('');

    if (paymentType !== 'class' || !checkoutSessionId) {
      setAppliedPromoCode(null);
      setDiscountAmount(0);
      return;
    }

    setIsValidatingPromo(true);
    updateClassCheckoutSession(checkoutSessionId, { promoCode: null })
      .then(hydratedCheckout => {
        applyHydratedCheckoutSession(hydratedCheckout);
      })
      .catch(error => {
        toast({
          title: 'Promo Update Failed',
          description: error instanceof Error ? error.message : 'Failed to remove promo code.',
          variant: 'destructive',
        });
      })
      .finally(() => {
        setIsValidatingPromo(false);
      });
  };

  const handleCustomerAmountChange = async (amountStr: string) => {
    if (paymentType !== 'class' || !checkoutSessionId) return;
    const amount = parseFloat(amountStr) || 0;
    setIsUpdatingCustomerAmount(true);
    try {
      const hydratedCheckout = await updateClassCheckoutSession(checkoutSessionId, {
        customerAmount: amount,
      });
      applyHydratedCheckoutSession(hydratedCheckout);
    } catch {
      toast({
        title: 'Update Failed',
        description: 'Failed to update amount. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsUpdatingCustomerAmount(false);
    }
  };

  const handleQuantityChange = async (newQuantity: number) => {
    if (newQuantity < 1 || newQuantity > maxQuantity || isUpdatingQuantity) return;

    setIsUpdatingQuantity(true);

    // Only update payment intent for class purchases
    if (paymentType === 'class') {
      updateQuantityMutation.mutate(newQuantity);
    } else {
      setIsUpdatingQuantity(false);
    }
  };

  const formatDate = (dateString: string) => {
    return formatDateWithTimezone(dateString, { dateOnly: true });
  };

  const formatTime = (dateString: string) => {
    return formatDateWithTimezone(dateString, { timeOnly: true });
  };

  if (authLoading) {
    return (
      <PublicShell routeKey="checkout">
        <div className="page-shell flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div>
        </div>
      </PublicShell>
    );
  }

  if (!isAuthenticated) {
    return null; // Will redirect via useEffect
  }

  if (tipCompleted) {
    return (
      <PublicShell routeKey="checkout">
        <section className="page-shell-narrow flex min-h-[60vh] items-center justify-center py-12">
          <Card className="w-full max-w-md rounded-[1.75rem] border-border/70 shadow-sm">
            <CardContent className="space-y-5 p-8 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
                <Check className="h-7 w-7 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold text-foreground">Tip sent!</h1>
                <p className="mt-2 text-sm text-muted-foreground">
                  {tipCompleted.amountCents
                    ? `Your $${(tipCompleted.amountCents / 100).toFixed(2)} tip is on its way to the creator.`
                    : 'Your tip is on its way to the creator.'}{' '}
                  Thank you for supporting them — a receipt is headed to your inbox.
                </p>
              </div>
              <div className="flex flex-col gap-2">
                {tipCompleted.creatorId && (
                  <Button
                    className="w-full rounded-full"
                    onClick={() => navigate(`/creators/${tipCompleted.creatorId}`)}
                    data-testid="button-tip-back-to-creator"
                  >
                    Back to creator
                  </Button>
                )}
                <Button
                  variant="outline"
                  className="w-full rounded-full"
                  onClick={() => navigate('/')}
                >
                  Browse classes
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>
      </PublicShell>
    );
  }

  if (passCompleted) {
    return (
      <PublicShell routeKey="checkout">
        <section className="page-shell-narrow flex min-h-[60vh] items-center justify-center py-12">
          <Card className="w-full max-w-md rounded-[1.75rem] border-border/70 shadow-sm">
            <CardContent className="space-y-5 p-8 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
                <Check className="h-7 w-7 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold text-foreground">Pass purchased!</h1>
                <p className="mt-2 text-sm text-muted-foreground">
                  {passCompleted.amountCents
                    ? `Your $${(passCompleted.amountCents / 100).toFixed(2)} punch pass is ready.`
                    : 'Your punch pass is ready.'}{' '}
                  Open the class to use a punch on any upcoming session.
                </p>
              </div>
              <div className="flex flex-col gap-2">
                {passCompleted.classId && (
                  <Button
                    className="w-full rounded-full"
                    onClick={() => navigate(`/classes/${passCompleted.classId}`)}
                    data-testid="button-pass-back-to-class"
                  >
                    Go to class
                  </Button>
                )}
                <Button
                  variant="outline"
                  className="w-full rounded-full"
                  onClick={() => navigate('/')}
                >
                  Browse classes
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>
      </PublicShell>
    );
  }

  if (isCheckoutLoading) {
    return (
      <PublicShell routeKey="checkout">
        <div className="page-shell flex items-center justify-center min-h-[60vh]">
          <div
            className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"
            aria-label="Loading"
          />
        </div>
      </PublicShell>
    );
  }

  if (!itemInfo) {
    return (
      <PublicShell routeKey="checkout">
        <div className="page-shell-narrow flex min-h-[50vh] items-center justify-center">
          <p className="text-sm text-destructive">Checkout information could not be loaded.</p>
        </div>
      </PublicShell>
    );
  }

  if (!isFreeOrder && !clientSecret) {
    return (
      <PublicShell routeKey="checkout">
        <div className="page-shell flex items-center justify-center min-h-[60vh]">
          <div
            className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"
            aria-label="Loading"
          />
        </div>
      </PublicShell>
    );
  }

  if (!stripePromise && !isFreeOrder) {
    return (
      <PublicShell routeKey="checkout">
        <div className="page-shell-narrow flex min-h-[50vh] items-center justify-center">
          <p className="text-sm text-destructive">
            Payment system is unavailable. Please contact support.
          </p>
        </div>
      </PublicShell>
    );
  }

  return (
    <PublicShell routeKey="checkout">
      <section className="page-shell-narrow py-12">
        <div className="mb-8">
          <p className="editorial-kicker">
            {paymentType === 'tip'
              ? 'Creator support'
              : paymentType === 'pass'
                ? 'Punch pass'
                : 'Secure checkout'}
          </p>
          <h1 className="mt-3 text-4xl font-semibold">
            {paymentType === 'tip'
              ? 'Send a tip'
              : paymentType === 'pass'
                ? 'Buy a punch pass'
                : 'Complete your booking'}
          </h1>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
          {/* Order Details */}
          <div className="space-y-5">
            {/* Cart Summary */}
            <Card className="rounded-[1.75rem] border-border/70 shadow-sm">
              <CardContent className="space-y-5 p-6">
                {itemInfo && (
                  <>
                    {paymentType === 'class' ? (
                      <div className="flex gap-4">
                        {(itemInfo as ClassItemInfo).thumbnailUrl ? (
                          <img
                            src={normalizeImageUrl(
                              (itemInfo as ClassItemInfo).thumbnailUrl,
                              PLACEHOLDER.thumb
                            )}
                            alt={itemInfo.title}
                            className="h-16 w-24 flex-shrink-0 rounded-xl object-cover"
                            data-testid="checkout-item-image"
                            onError={imageErrorFallback(PLACEHOLDER.thumb, 'Class Thumbnail')}
                          />
                        ) : (
                          <div className="h-16 w-24 flex-shrink-0 rounded-xl bg-muted" />
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-muted-foreground">
                            {(itemInfo as ClassItemInfo).creator?.displayName}
                          </p>
                          <h2
                            className="mt-0.5 text-xl font-semibold leading-snug text-foreground"
                            data-testid="checkout-item-title"
                          >
                            {itemInfo.title}
                          </h2>
                          {(itemInfo as ClassItemInfo).category && (
                            <Badge
                              variant="outline"
                              className="mt-2 rounded-full px-3 py-0.5 text-xs"
                            >
                              {(itemInfo as ClassItemInfo).category}
                            </Badge>
                          )}
                        </div>
                      </div>
                    ) : paymentType === 'pass' ? (
                      <div className="flex items-center gap-4">
                        <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-full bg-primary/10">
                          <Coffee className="h-7 w-7 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Punch pass for</p>
                          <h2
                            className="mt-0.5 text-xl font-semibold text-foreground"
                            data-testid="checkout-pass-title"
                          >
                            {itemInfo.title}
                          </h2>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-4">
                        <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-full bg-primary/10">
                          <Coffee className="h-7 w-7 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Supporting</p>
                          <h2
                            className="mt-0.5 text-xl font-semibold text-foreground"
                            data-testid="checkout-tip-title"
                          >
                            {(itemInfo as TipItemInfo).displayName}
                          </h2>
                        </div>
                      </div>
                    )}

                    {/* Quantity Controls */}
                    {paymentType === 'class' && (
                      <>
                        <Separator />
                        <div className="flex items-center justify-between">
                          <span className="font-medium">Tickets</span>
                          <div className="flex items-center gap-3">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleQuantityChange(quantity - 1)}
                              disabled={quantity <= 1 || isUpdatingQuantity}
                              className="h-9 w-9 rounded-full p-0"
                            >
                              <Minus className="h-4 w-4" />
                            </Button>
                            <span className="w-8 text-center font-semibold">{quantity}</span>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleQuantityChange(quantity + 1)}
                              disabled={quantity >= maxQuantity || isUpdatingQuantity}
                              className="h-9 w-9 rounded-full p-0"
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </>
                    )}

                    {needsGuestNames && (
                      <>
                        <Separator />
                        <div className="space-y-3">
                          <div className="space-y-1">
                            <p className="font-medium">Guest names</p>
                            <p className="text-sm text-muted-foreground">
                              Add one name for each additional seat in this order.
                            </p>
                          </div>
                          {Array.from({ length: requiredGuestCount }).map((_, index) => (
                            <div key={`guest-${index}`}>
                              <label
                                className="text-sm font-medium text-foreground"
                                htmlFor={`guest-name-${index}`}
                              >
                                Guest {index + 1}
                              </label>
                              <Input
                                id={`guest-name-${index}`}
                                value={attendeeDrafts[index]?.name ?? ''}
                                onChange={e => handleAttendeeDraftChange(index, e.target.value)}
                                placeholder={`Guest ${index + 1} full name`}
                                className="mt-1 rounded-xl"
                                data-testid={`input-guest-name-${index + 1}`}
                              />
                            </div>
                          ))}
                        </div>
                      </>
                    )}

                    <Separator />

                    {/* PWYW Amount */}
                    {paymentType === 'class' &&
                      (itemInfo as ClassItemInfo | null)?.pricingMode === 'pay_what_you_want' && (
                        <div className="space-y-3">
                          <p className="text-sm font-medium text-foreground">Your contribution</p>
                          {(itemInfo as ClassItemInfo).suggestedPrice &&
                            Number((itemInfo as ClassItemInfo).suggestedPrice) > 0 && (
                              <p className="text-xs text-muted-foreground">
                                Suggested: $
                                {Number((itemInfo as ClassItemInfo).suggestedPrice).toFixed(0)}
                              </p>
                            )}
                          <div className="flex gap-2">
                            <Input
                              type="number"
                              min="0"
                              step="1"
                              placeholder="Enter amount"
                              value={customerAmount}
                              onChange={e => setCustomerAmount(e.target.value)}
                              className="flex-1 rounded-xl"
                              data-testid="input-customer-amount"
                            />
                            <Button
                              variant="outline"
                              onClick={() => handleCustomerAmountChange(customerAmount)}
                              disabled={isUpdatingCustomerAmount || !customerAmount}
                              className="rounded-full px-5"
                            >
                              {isUpdatingCustomerAmount ? 'Updating...' : 'Set'}
                            </Button>
                          </div>
                        </div>
                      )}

                    {/* Promo Code */}
                    {paymentType === 'class' && (
                      <div className="space-y-3">
                        <p className="text-sm font-medium text-foreground">Promo code</p>
                        {!appliedPromoCode ? (
                          <div className="flex gap-2">
                            <Input
                              placeholder="Enter code"
                              value={promoCode}
                              onChange={e => setPromoCode(e.target.value.toUpperCase())}
                              onKeyPress={e => e.key === 'Enter' && handleApplyPromoCode()}
                              disabled={isValidatingPromo}
                              className="flex-1 rounded-xl"
                            />
                            <Button
                              variant="outline"
                              onClick={handleApplyPromoCode}
                              disabled={isValidatingPromo || !promoCode.trim()}
                              className="rounded-full px-5"
                            >
                              {isValidatingPromo ? 'Checking...' : 'Apply'}
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between rounded-xl border border-primary/20 bg-primary/10 px-4 py-3">
                            <div className="flex items-center gap-2">
                              <Check className="h-4 w-4 text-primary" />
                              <span className="text-sm font-medium text-primary">
                                {appliedPromoCode.code}
                              </span>
                              <span className="text-sm text-muted-foreground">
                                –
                                {appliedPromoCode.discountType === 'percentage'
                                  ? `${appliedPromoCode.discountValue}% off`
                                  : `$${appliedPromoCode.discountValue} off`}
                              </span>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={handleRemovePromoCode}
                              className="h-7 w-7 rounded-full p-0 text-muted-foreground hover:text-foreground"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                        {promoError && (
                          <p className="rounded-xl border border-destructive/20 bg-destructive/10 px-4 py-2 text-sm text-destructive">
                            {promoError}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Promo Code — punch pass */}
                    {paymentType === 'pass' && (
                      <div className="space-y-3">
                        <p className="text-sm font-medium text-foreground">Promo code</p>
                        {!passPromoApplied ? (
                          <div className="flex gap-2">
                            <Input
                              placeholder="Enter code"
                              value={promoCode}
                              onChange={e => setPromoCode(e.target.value.toUpperCase())}
                              onKeyPress={e => e.key === 'Enter' && handleApplyPassPromo()}
                              disabled={isValidatingPromo}
                              className="flex-1 rounded-xl"
                              data-testid="input-pass-promo"
                            />
                            <Button
                              variant="outline"
                              onClick={handleApplyPassPromo}
                              disabled={isValidatingPromo || !promoCode.trim()}
                              className="rounded-full px-5"
                              data-testid="button-apply-pass-promo"
                            >
                              {isValidatingPromo ? 'Checking...' : 'Apply'}
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between rounded-xl border border-primary/20 bg-primary/10 px-4 py-3">
                            <div className="flex items-center gap-2">
                              <Check className="h-4 w-4 text-primary" />
                              <span className="text-sm font-medium text-primary">
                                {passPromoApplied}
                              </span>
                              <span className="text-sm text-muted-foreground">
                                –${discountAmount.toFixed(2)} off
                              </span>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={handleRemovePassPromo}
                              disabled={isValidatingPromo}
                              className="h-7 w-7 rounded-full p-0 text-muted-foreground hover:text-foreground"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                        {promoError && (
                          <p className="rounded-xl border border-destructive/20 bg-destructive/10 px-4 py-2 text-sm text-destructive">
                            {promoError}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Price Summary */}
                    <div className="rounded-[1.25rem] border border-border/60 bg-muted/30 p-4">
                      <div className="space-y-2">
                        {quantity > 1 && (
                          <div className="flex justify-between text-sm text-muted-foreground">
                            <span>
                              ${unitPrice} × {quantity}
                            </span>
                            <span>${totalAmount.toFixed(2)}</span>
                          </div>
                        )}
                        {appliedPromoCode && discountAmount > 0 && (
                          <div className="flex justify-between text-sm text-primary">
                            <span>Promo ({appliedPromoCode.code})</span>
                            <span>–${discountAmount.toFixed(2)}</span>
                          </div>
                        )}
                        {passPromoApplied && discountAmount > 0 && (
                          <div className="flex justify-between text-sm text-primary">
                            <span>Promo ({passPromoApplied})</span>
                            <span>–${discountAmount.toFixed(2)}</span>
                          </div>
                        )}
                        <div className="flex justify-between text-lg font-semibold text-foreground">
                          <span>Total</span>
                          <span data-testid="checkout-item-price">
                            {isFreeOrder ? 'Free' : `$${finalTotal.toFixed(2)}`}
                          </span>
                        </div>
                      </div>
                    </div>

                    {sessionInfo && maxQuantity < 10 && (
                      <div className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                        <Users className="h-4 w-4 flex-shrink-0" />
                        <span>Only {maxQuantity} spots remaining for this session</span>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Session Details */}
            {sessionInfo && paymentType === 'class' && (
              <Card className="rounded-[1.75rem] border-border/70 shadow-sm">
                <CardContent className="space-y-4 p-6">
                  <p className="editorial-kicker">Session details</p>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <Calendar className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                      <div>
                        <p className="font-medium">{formatDate(sessionInfo.startDateTime)}</p>
                        <p className="text-sm text-muted-foreground">
                          {formatTime(sessionInfo.startDateTime)} –{' '}
                          {formatTime(sessionInfo.endDateTime)}
                        </p>
                      </div>
                    </div>
                    {(itemInfo as ClassItemInfo)?.location && (
                      <div className="flex items-start gap-3">
                        <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                        <p className="text-sm text-muted-foreground">
                          {(itemInfo as ClassItemInfo).location}
                        </p>
                      </div>
                    )}
                    <div className="flex items-start gap-3">
                      <Users className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                      <p className="text-sm text-muted-foreground">
                        {sessionInfo.availableSpots == null
                          ? 'Open availability'
                          : `${sessionInfo.availableSpots} spots remaining`}
                      </p>
                    </div>
                  </div>

                  {(itemInfo as ClassItemInfo)?.cancellationPolicy && (
                    <div className="rounded-[1.25rem] border border-border/60 bg-muted/30 p-4">
                      <p className="text-sm font-medium text-foreground">
                        {getCancellationPolicyLabel(
                          (itemInfo as ClassItemInfo).cancellationPolicy as CancellationPolicy
                        )}
                      </p>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {getCancellationPolicyDescription(
                          (itemInfo as ClassItemInfo).cancellationPolicy as CancellationPolicy
                        )}
                      </p>
                    </div>
                  )}

                  {sessionInfo.notes && (
                    <div className="rounded-[1.25rem] border border-border/60 bg-muted/30 p-4">
                      <p className="text-sm font-medium text-foreground">Session notes</p>
                      <p className="mt-1 text-sm text-muted-foreground">{sessionInfo.notes}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Payment Form or Free Enrollment */}
          <div className="space-y-4">
            <Card className="rounded-[1.75rem] border-border/70 shadow-sm">
              <CardContent className="p-6">
                {paymentType === 'pass' && passIsFree ? (
                  <div className="space-y-5">
                    <div className="rounded-[1.25rem] border border-primary/20 bg-primary/10 p-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Check className="h-5 w-5 text-primary" />
                        <span className="font-semibold text-primary">
                          Your promo covers this pass
                        </span>
                      </div>
                      <p className="mt-2 text-sm text-muted-foreground">
                        No payment needed — tap below to get your punch pass.
                      </p>
                    </div>
                    <Button
                      onClick={() => {
                        void handleCompleteFreePass();
                      }}
                      disabled={isCompletingFreePass}
                      className="w-full rounded-full py-6 text-base font-semibold"
                      data-testid="button-complete-free-pass"
                    >
                      <Check className="mr-2 h-5 w-5" />
                      {isCompletingFreePass ? 'Processing...' : 'Get free pass'}
                    </Button>
                  </div>
                ) : isFreeOrder ? (
                  <div className="space-y-5">
                    <div className="rounded-[1.25rem] border border-primary/20 bg-primary/10 p-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Check className="h-5 w-5 text-primary" />
                        <span className="font-semibold text-primary">This class is free</span>
                      </div>
                      <p className="mt-2 text-sm text-muted-foreground">
                        No payment needed — tap below to confirm your spot.
                      </p>
                    </div>
                    {needsGuestNames && !attendeeDraftsComplete && (
                      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                        Complete the guest name fields before confirming enrollment.
                      </div>
                    )}
                    <Button
                      onClick={() => {
                        void handleFreeEnrollment();
                      }}
                      disabled={isProcessing || (needsGuestNames && !attendeeDraftsComplete)}
                      className="w-full rounded-full py-6 text-base font-semibold"
                      data-testid="button-complete-free-enrollment"
                    >
                      <Check className="mr-2 h-5 w-5" />
                      {isProcessing ? 'Processing...' : 'Confirm free enrollment'}
                    </Button>
                  </div>
                ) : clientSecret ? (
                  <div className="space-y-5">
                    <p className="editorial-kicker">Payment</p>
                    <Elements
                      key={clientSecret}
                      stripe={stripePromise}
                      options={{
                        clientSecret,
                        appearance: {
                          theme: 'stripe',
                          variables: {
                            colorPrimary: 'hsl(214, 100%, 59%)',
                            colorBackground: 'hsl(0, 0%, 100%)',
                            colorText: 'hsl(222, 84%, 4.9%)',
                            colorDanger: 'hsl(0, 84%, 60%)',
                            fontFamily: 'Inter, system-ui, sans-serif',
                            spacingUnit: '4px',
                            borderRadius: '12px',
                          },
                        },
                      }}
                    >
                      <CheckoutForm
                        checkoutSessionId={paymentType === 'class' ? checkoutSessionId : undefined}
                        isProcessing={isProcessing}
                        setIsProcessing={setIsProcessing}
                        isBlocked={needsGuestNames && !attendeeDraftsComplete}
                        onBeforeSubmit={saveAttendeeDrafts}
                        returnUrl={
                          paymentType === 'tip' && itemInfo
                            ? `${window.location.origin}/checkout?type=tip&creator_id=${itemInfo.id}&amount=${Math.round(finalTotal * 100)}`
                            : paymentType === 'pass' && itemInfo
                              ? `${window.location.origin}/checkout?type=pass&class_id=${itemInfo.id}&amount=${Math.round(finalTotal * 100)}`
                              : undefined
                        }
                      />
                    </Elements>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center gap-3 py-8">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                    <p className="text-sm text-muted-foreground">Loading payment details...</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {!isFreeOrder && (
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Lock className="h-4 w-4" />
                <span>Payment secured by Stripe</span>
              </div>
            )}
          </div>
        </div>
      </section>
    </PublicShell>
  );
}
